import { useCallback, useEffect, useState } from "react";
import { Download, SlidersHorizontal } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { PageHead, EmptyState } from "@/components/Bits";
import { downloadCsv } from "@/lib/csv";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const REASONS = [
  ["all", "All reasons"],
  ["opening_stock_correction", "Opening stock correction"],
  ["damage", "Damage"],
  ["loss", "Loss"],
  ["count_correction", "Count correction"],
  ["other", "Other"],
];
const reasonLabel = (r) => (REASONS.find(([v]) => v === r) || [r, r])[1];

export default function AdjustmentsLog() {
  const [studios, setStudios] = useState([]);
  const [studioId, setStudioId] = useState("all");
  const [reason, setReason] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get("/studios").then(({ data }) => setStudios(data)).catch(() => {});
  }, []);
  const studioName = (id) => studios.find((s) => s.id === id)?.name || id;

  const load = useCallback(() => {
    const params = {};
    if (studioId !== "all") params.studio_id = studioId;
    if (reason !== "all") params.reason = reason;
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    api.get("/stock/adjustments", { params }).then(({ data }) => setRows(data || [])).catch(() => setRows([]));
  }, [studioId, reason, from, to]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    downloadCsv(`stock-adjustments-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((a) => ({
        When: fmtDate(a.timestamp),
        Studio: studioName(a.studio_id),
        Product: a.sku_name,
        Change: a.delta,
        Unit: a.unit || "",
        "Before": a.before_qty,
        "After": a.after_qty,
        Reason: reasonLabel(a.reason),
        Note: a.note || "",
        "By": a.user_name,
        Role: a.role,
      })));
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="adjustments-log-page">
      <PageHead title="Adjustments Log" subtitle="Every stock adjustment with reason, who made it and when">
        <Button variant="outline" data-testid="adjust-export-btn" onClick={exportCsv} disabled={!rows.length}>
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </PageHead>

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-5">
        <div>
          <Label>Studio</Label>
          <Select value={studioId} onValueChange={setStudioId}>
            <SelectTrigger data-testid="adjust-studio-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios</SelectItem>
              {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Reason</Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="adjust-reason-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>From</Label>
          <Input type="date" data-testid="adjust-from" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <Label>To</Label>
          <Input type="date" data-testid="adjust-to" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="flex items-end">
          {(from || to || reason !== "all" || studioId !== "all") && (
            <Button variant="ghost" data-testid="adjust-clear"
              onClick={() => { setStudioId("all"); setReason("all"); setFrom(""); setTo(""); }}>
              Clear
            </Button>
          )}
        </div>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardContent className="overflow-x-auto p-0">
          {rows.length === 0 ? (
            <EmptyState icon={SlidersHorizontal} title="No adjustments found" hint="No stock adjustments match these filters." />
          ) : (
            <Table data-testid="adjustments-table">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Studio</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead className="text-right">Change</TableHead>
                  <TableHead className="text-right">Before → After</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id} data-testid={`adjust-row-${a.id}`}>
                    <TableCell className="whitespace-nowrap">{fmtDate(a.timestamp)}</TableCell>
                    <TableCell>{studioName(a.studio_id)}</TableCell>
                    <TableCell>{a.sku_name}</TableCell>
                    <TableCell className={`text-right font-medium ${a.delta < 0 ? "text-rose-600" : "text-emerald-600"}`}>
                      {a.delta > 0 ? "+" : ""}{a.delta} {a.unit || ""}
                    </TableCell>
                    <TableCell className="text-right text-slate-600">{a.before_qty} → {a.after_qty}</TableCell>
                    <TableCell>
                      <Badge className="border-0 bg-slate-100 text-slate-700">{reasonLabel(a.reason)}</Badge>
                      {a.note && <span className="ml-2 text-xs text-slate-500">{a.note}</span>}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-slate-600">
                      {a.user_name} <span className="text-xs text-slate-400">({a.role})</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
